import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ChecklistItem from '../settlement/ChecklistItem';
import DocumentUploadSection from '../settlement/DocumentUploadSection';
import ProgressHeader from '../settlement/ProgressHeader';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import {
  RESIDENCE_SECURE_PANELS,
  loadResidencePanelCompletion,
  defaultSecureResidencePayload,
  isPanelGranularComplete,
  normalizeSecureResidenceData,
  withEstatePath,
  type ResidencePanelDefinition,
  type SecureResidenceFramePayload,
} from '../../utils/secureResidenceFlow';

const PHASE = 'iad';
const STEP_ID = 'iad-2';
const HUB_ROUTE = '/sframe-134';
const NEXT_IAD_STEP_ROUTE = '/sframe-137';

interface SecureResidenceSubFrameProps {
  panel: ResidencePanelDefinition;
}

export default function SecureResidenceSubFrame({ panel }: SecureResidenceSubFrameProps) {
  const { estateId: paramEstateId } = useParams<{ estateId: string }>();
  const estateId = paramEstateId || null;
  const navigate = useNavigate();

  const {
    isLoading,
    error,
    loadFrameData,
    saveFrameData,
    loadPhaseProgress,
    updateStepProgress,
    toggleChecklistItem,
  } = useSettlementSession(estateId);

  const [payload, setPayload] = useState<SecureResidenceFramePayload>(() => defaultSecureResidencePayload());
  const [completedPanels, setCompletedPanels] = useState(0);
  const [hydrated, setHydrated] = useState(false);

  const refreshAggregate = useCallback(async () => {
    const { completedCount } = await loadResidencePanelCompletion(estateId, loadFrameData);
    setCompletedPanels(completedCount);
  }, [estateId, loadFrameData]);

  useEffect(() => {
    if (!estateId) {
      setHydrated(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const row = await loadFrameData(panel.frameId);
      if (cancelled) return;
      const next = normalizeSecureResidenceData(row?.data);
      setPayload(next);

      const granularDone = isPanelGranularComplete(panel, next);
      if (granularDone) {
        await toggleChecklistItem(STEP_ID, PHASE, panel.macroChecklistItemId, true);
      }
      const { completedCount } = await loadResidencePanelCompletion(estateId, loadFrameData);
      if (!cancelled) setCompletedPanels(completedCount);
      setHydrated(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, panel, loadFrameData, toggleChecklistItem]);

  const persistPayload = useCallback(
    async (next: SecureResidenceFramePayload) => {
      if (!estateId) return;
      setPayload(next);
      await saveFrameData(panel.frameId, next as unknown as Record<string, unknown>);
    },
    [estateId, panel.frameId, saveFrameData]
  );

  const ensureStepInProgress = useCallback(async () => {
    if (!estateId) return;
    const steps = await loadPhaseProgress(PHASE);
    const row = steps.find((s) => s.stepId === STEP_ID);
    if (row?.status === 'not-started' || !row) {
      await updateStepProgress(STEP_ID, PHASE, { status: 'in-progress' });
    }
  }, [estateId, loadPhaseProgress, updateStepProgress]);

  const handleChecklistChange = useCallback(
    async (itemId: string, checked: boolean) => {
      await ensureStepInProgress();
      const checklist = { ...payload.checklist, [itemId]: checked };
      const next = { ...payload, checklist };
      await persistPayload(next);

      const granularDone = isPanelGranularComplete(panel, next);
      if (estateId) {
        await toggleChecklistItem(STEP_ID, PHASE, panel.macroChecklistItemId, granularDone);
      }
      await refreshAggregate();
    },
    [
      ensureStepInProgress,
      payload,
      persistPayload,
      panel,
      estateId,
      toggleChecklistItem,
      refreshAggregate,
    ]
  );

  const handleDocUpload = useCallback(
    async (file: File) => {
      await ensureStepInProgress();
      const doc = {
        id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `doc-${Date.now()}`,
        name: file.name,
        type: file.type || 'application/octet-stream',
        size: file.size,
        uploadedAt: new Date().toISOString(),
      };
      const next = { ...payload, documents: [...payload.documents, doc] };
      await persistPayload(next);
    },
    [ensureStepInProgress, payload, persistPayload]
  );

  const handleDocDelete = useCallback(
    async (docId: string) => {
      const next = { ...payload, documents: payload.documents.filter((d) => d.id !== docId) };
      await persistPayload(next);
    },
    [payload, persistPayload]
  );

  const idx = panel.progressIndex;
  const prevPanel = idx > 0 ? RESIDENCE_SECURE_PANELS[idx - 1] : null;
  const nextPanel = idx < RESIDENCE_SECURE_PANELS.length - 1 ? RESIDENCE_SECURE_PANELS[idx + 1] : null;

  const goHub = () => navigate(withEstatePath(HUB_ROUTE, estateId));
  const goPrev = () => {
    if (prevPanel) navigate(withEstatePath(prevPanel.path, estateId));
    else goHub();
  };
  const goNext = () => {
    if (nextPanel) navigate(withEstatePath(nextPanel.path, estateId));
    else navigate(withEstatePath(NEXT_IAD_STEP_ROUTE, estateId));
  };

  if (!estateId) {
    return (
      <div className="max-w-3xl mx-auto">
        <p className="text-stone-700 mb-4">
          Select an estate from the settlement dashboard to save progress for this step.
        </p>
        <button
          type="button"
          onClick={() => navigate('/sframe-117')}
          className="text-blue-700 font-medium hover:underline"
        >
          Go to dashboard
        </button>
      </div>
    );
  }

  if (!hydrated) {
    return (
      <div className="max-w-3xl mx-auto text-grey-600 text-sm py-8" aria-live="polite">
        Loading…
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 bg-orange-100 border border-orange-300 rounded-md p-3 text-orange-900 text-sm max-w-3xl mx-auto">
          {error}
        </div>
      )}

      <div className="max-w-3xl mx-auto">
        <ProgressHeader
          title="Secure the residence"
          subtitle="Immediately After Death · Step 2 of 7"
          completedSteps={completedPanels}
          totalSteps={RESIDENCE_SECURE_PANELS.length}
          estimatedMinutes={60}
        />

        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={goPrev}
            className="w-[35px] h-[35px] flex items-center justify-center text-grey-600 hover:text-blue-900 transition-colors"
            aria-label={prevPanel ? 'Previous section' : 'Back to residence overview'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-grey-600">Secure property</p>
            <h1 className="font-display text-[28px] leading-tight text-blue-900">{panel.title}</h1>
          </div>
        </div>

        <p className="text-sm text-grey-600 mb-8 leading-relaxed">{panel.intro}</p>

        <div className="space-y-3 mb-8">
          {panel.checklist.map((item) => (
            <ChecklistItem
              key={item.id}
              id={item.id}
              title={item.title}
              description={item.description}
              checked={!!payload.checklist[item.id]}
              onChange={handleChecklistChange}
            />
          ))}
        </div>

        <DocumentUploadSection
          documents={payload.documents}
          onUpload={handleDocUpload}
          onDelete={handleDocDelete}
          className="mb-10"
        />

        <div className="flex flex-wrap gap-3 pb-8">
          <button
            type="button"
            onClick={goHub}
            className="px-5 py-2.5 rounded-lg border border-grey-300 text-sm font-medium text-blue-900 hover:bg-grey-50"
          >
            Overview
          </button>
          <button
            type="button"
            onClick={goNext}
            className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
          >
            {nextPanel ? 'Continue' : 'Next IAD step'}
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="text-center text-grey-600 text-sm py-2">Saving…</div>
      )}
    </div>
  );
}
